// src/pages/AdminDashboard.jsx
import { useState, useEffect } from "react";
import {
  collection, query, where, doc, updateDoc,
  onSnapshot, Timestamp
} from "firebase/firestore";
import { db, firebaseConfig } from "../firebase";
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import {
  Users, UserPlus, AlertTriangle, Shield,
  Clock, CheckCircle, XCircle,
  Calendar, Mail, Phone, Lock, Eye, EyeOff,
  ChevronDown, ChevronUp, RefreshCw, Stethoscope,
  Ban, PlayCircle, Timer
} from "lucide-react";


export default function AdminDashboard() {
  const { profile } = useAuth();
  const { lang, t } = useLanguage();
  const [tab, setTab] = useState("doctors");

  return (
    <div className="page-content fade-in">
      <div className="container">
        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem"
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <Shield size={24} style={{ color: "#6366f1" }} />
              <h2 style={{ margin: 0 }}>{t("adminDashboard")}</h2>
            </div>
            <p className="text-muted text-sm" style={{ margin: 0 }}>
              {lang === "ar"
                ? `مرحباً، ${profile?.name || ""}`
                : `Welcome, ${profile?.name || ""}`}
            </p>
          </div>
          <div className="badge" style={{
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            color: "#fff", padding: "0.35rem 0.9rem", fontSize: "0.82rem"
          }}>
            {lang === "ar" ? "مدير" : "Admin"}
          </div>
        </div>

        {/* ── Tab Nav ── */}
        <div style={{
          display: "flex", gap: "0.4rem", background: "linear-gradient(135deg, #4338ca, #6366f1)",
          borderRadius: "var(--radius-md)", padding: "0.3rem", marginBottom: "2rem",
          flexWrap: "wrap"
        }}>
          {[
            { key: "doctors", icon: <Stethoscope size={16} />, label: t("manageDoctors") },
            { key: "add",     icon: <UserPlus size={16} />,    label: t("addDoctor") },
            { key: "alerts",  icon: <AlertTriangle size={16} />, label: t("expirationAlerts") },
          ].map(item => (
            <button
              key={item.key}
              onClick={() => setTab(item.key)}
              className={`btn btn-sm flex items-center gap-2 ${tab === item.key ? "" : "btn-ghost"}`}
              style={{
                flex: "1 0 auto",
                ...(tab === item.key
                  ? { background: "#fff", color: "#4338ca", fontWeight: 700 }
                  : {})
              }}
            >
              {item.icon} {item.label}
            </button>
          ))}
        </div>

        {tab === "doctors" && <DoctorsTab lang={lang} t={t} />}
        {tab === "add"     && <AddDoctorTab lang={lang} t={t} profile={profile} />}
        {tab === "alerts"  && <AlertsTab lang={lang} t={t} />}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   DOCTORS LIST TAB
   ═══════════════════════════════════════════════ */
function DoctorsTab({ lang, t }) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const q = query(collection(db, "users"), where("role", "==", "doctor"));
    const unsub = onSnapshot(q, snap => {
      setDoctors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  // Stats
  const totalDoctors = doctors.length;
  const activeDoctors = doctors.filter(d => !d.suspended && d.status !== "expired").length;
  const suspendedDoctors = doctors.filter(d => d.suspended).length;

  const now = new Date();
  const sevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const expiringDoctors = doctors.filter(d => {
    if (d.isPermanent || d.suspended) return false;
    if (!d.accountExpiresAt) return false;
    const exp = d.accountExpiresAt.toDate ? d.accountExpiresAt.toDate() : new Date(d.accountExpiresAt);
    return exp > now && exp <= sevenDays;
  }).length;

  const filtered = doctors.filter(d => {
    if (filter === "active") return !d.suspended && d.status !== "expired";
    if (filter === "suspended") return d.suspended;
    if (filter === "expiring") {
      if (d.isPermanent || d.suspended) return false;
      if (!d.accountExpiresAt) return false;
      const exp = d.accountExpiresAt.toDate ? d.accountExpiresAt.toDate() : new Date(d.accountExpiresAt);
      return exp > now && exp <= sevenDays;
    }
    return true;
  });

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid-4" style={{ gap: "1rem", marginBottom: "1.5rem" }}>
        {[
          { label: t("totalDoctors"),     value: totalDoctors,     icon: <Users size={22} />,          color: "#6366f1" },
          { label: t("activeDoctors"),    value: activeDoctors,    icon: <CheckCircle size={22} />,    color: "#22c55e" },
          { label: t("suspendedDoctors"), value: suspendedDoctors, icon: <Ban size={22} />,            color: "#ef4444" },
          { label: t("expiringDoctors"),  value: expiringDoctors,  icon: <AlertTriangle size={22} />,  color: "#f59e0b" },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ borderTop: `3px solid ${s.color}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: s.color }}>{s.icon}</span>
            </div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter Buttons */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {[
          { key: "all",       label: lang === "ar" ? "الكل" : "All" },
          { key: "active",    label: t("active") },
          { key: "suspended", label: t("suspended") },
          { key: "expiring",  label: lang === "ar" ? "قارب الانتهاء" : "Expiring" },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`btn btn-sm ${filter === f.key ? "" : "btn-outline"}`}
            style={filter === f.key ? { background: "#6366f1", color: "#fff" } : {}}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Doctor Cards */}
      {filtered.length === 0 ? (
        <div className="text-center" style={{ padding: "3rem", color: "var(--color-gray-300)" }}>
          {t("noDoctors")}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {filtered.map(doctor => (
            <DoctorCard key={doctor.id} doctor={doctor} lang={lang} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Single Doctor Card ── */
function DoctorCard({ doctor, lang, t }) {
  const [expanded, setExpanded] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendValue, setExtendValue] = useState(1);
  const [extendUnit, setExtendUnit] = useState("months");
  const [actionLoading, setActionLoading] = useState(false);

  const now = new Date();

  // Calculate days left
  let daysLeft = null;
  let isExpiring = false;
  if (!doctor.isPermanent && doctor.accountExpiresAt) {
    const exp = doctor.accountExpiresAt.toDate ? doctor.accountExpiresAt.toDate() : new Date(doctor.accountExpiresAt);
    daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    isExpiring = daysLeft > 0 && daysLeft <= 7;
  }

  // Format date helper
  function formatDate(ts) {
    if (!ts) return t("neverLoggedIn");
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(lang === "ar" ? "en-US" : "en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  }

  // Status badge
  function getStatusBadge() {
    if (doctor.suspended) {
      return <span className="badge badge-error">{t("suspended")}</span>;
    }
    if (doctor.status === "expired") {
      return <span className="badge badge-warn">{t("expired")}</span>;
    }
    return <span className="badge badge-green">{t("active")}</span>;
  }

  // Toggle suspend/activate
  async function handleToggleSuspend() {
    if (doctor.suspended) {
      setActionLoading(true);
      await updateDoc(doc(db, "users", doctor.id), {
        suspended: false,
        status: "active"
      });
      setActionLoading(false);
    } else {
      if (window.confirm(t("confirmSuspend"))) {
        setActionLoading(true);
        await updateDoc(doc(db, "users", doctor.id), {
          suspended: true,
          status: "suspended"
        });
        setActionLoading(false);
      }
    }
  }

  // Extend duration
  async function handleExtend() {
    setActionLoading(true);
    const baseDate = doctor.accountExpiresAt
      ? (doctor.accountExpiresAt.toDate ? doctor.accountExpiresAt.toDate() : new Date(doctor.accountExpiresAt))
      : new Date();

    // If already expired, extend from now
    const startDate = baseDate > now ? new Date(baseDate) : new Date(now);

    let newExpiry;
    switch (extendUnit) {
      case "days":
        newExpiry = new Date(startDate.getTime() + extendValue * 24 * 60 * 60 * 1000);
        break;
      case "weeks":
        newExpiry = new Date(startDate.getTime() + extendValue * 7 * 24 * 60 * 60 * 1000);
        break;
      case "months":
        newExpiry = new Date(startDate);
        newExpiry.setMonth(newExpiry.getMonth() + parseInt(extendValue));
        break;
      case "years":
        newExpiry = new Date(startDate);
        newExpiry.setFullYear(newExpiry.getFullYear() + parseInt(extendValue));
        break;
      case "permanent":
        await updateDoc(doc(db, "users", doctor.id), {
          isPermanent: true,
          accountExpiresAt: null,
          accountDurationUnit: "permanent",
          suspended: false,
          status: "active"
        });
        setActionLoading(false);
        setExtending(false);
        return;
      default:
        newExpiry = new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);
    }

    await updateDoc(doc(db, "users", doctor.id), {
      accountExpiresAt: Timestamp.fromDate(newExpiry),
      accountDurationUnit: extendUnit,
      accountDurationValue: parseInt(extendValue),
      suspended: false,
      status: "active"
    });

    setActionLoading(false);
    setExtending(false);
  }

  return (
    <div className="card card-sm" style={{
      borderRight: isExpiring ? "4px solid #f59e0b" : doctor.suspended ? "4px solid #ef4444" : "4px solid #22c55e",
      transition: "all 0.2s"
    }}>
      {/* ── Top Row ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, fontSize: "0.85rem", flexShrink: 0
            }}>
              {doctor.name?.[0]?.toUpperCase() || "D"}
            </div>
            <span style={{ fontWeight: 700, fontSize: "1rem" }}>{doctor.name}</span>
            {getStatusBadge()}
            {isExpiring && (
              <span className="badge badge-warn" style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                <AlertTriangle size={12} />
                {daysLeft} {t("day")}
              </span>
            )}
            {doctor.isPermanent && (
              <span className="badge badge-blue">{t("permanentAccount")}</span>
            )}
          </div>
          <div style={{ fontSize: "0.82rem", color: "var(--color-gray-500)", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Mail size={13} /> {doctor.email}
            </span>
            {doctor.phone && (
              <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                <Phone size={13} /> {doctor.phone}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            onClick={handleToggleSuspend}
            disabled={actionLoading}
            className={`btn btn-sm ${doctor.suspended ? "btn-primary" : ""}`}
            style={doctor.suspended
              ? { background: "#22c55e", borderColor: "#22c55e", color: "#fff" }
              : { background: "#fef2f2", borderColor: "#fca5a5", color: "#ef4444", border: "1.5px solid #fca5a5" }
            }
          >
            {actionLoading ? <div className="spinner" style={{ width: 14, height: 14 }} /> : (
              doctor.suspended
                ? <><PlayCircle size={14} /> {t("activate")}</>
                : <><Ban size={14} /> {t("suspend")}</>
            )}
          </button>

          <button
            onClick={() => setExtending(!extending)}
            className="btn btn-sm btn-outline"
            style={{ borderColor: "#6366f1", color: "#6366f1" }}
          >
            <Timer size={14} /> {t("extendDuration")}
          </button>

          <button
            onClick={() => setExpanded(s => !s)}
            className="btn btn-sm"
            style={{
              background: expanded ? "#6366f1" : "#f5f5ff",
              color: expanded ? "#fff" : "#6366f1",
              border: "1.5px solid #6366f1"
            }}
          >
            {expanded
              ? <><ChevronUp size={14} /> {lang === "ar" ? "إخفاء" : "Hide"}</>
              : <><ChevronDown size={14} /> {lang === "ar" ? "تفاصيل" : "Details"}</>
            }
          </button>
        </div>
      </div>

      {/* ── Extend Duration Panel ── */}
      {extending && (
        <div className="slide-down" style={{
          marginTop: "1rem", padding: "1rem",
          background: "#f5f3ff", borderRadius: "var(--radius-md)",
          display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap"
        }}>
          <div>
            <label className="form-label" style={{ fontSize: "0.78rem" }}>{t("durationValue")}</label>
            <input
              type="number"
              min="1"
              value={extendValue}
              onChange={e => setExtendValue(e.target.value)}
              className="form-input"
              style={{ width: 80 }}
            />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: "0.78rem" }}>{t("durationUnit")}</label>
            <select
              value={extendUnit}
              onChange={e => setExtendUnit(e.target.value)}
              className="form-select"
              style={{ width: 130 }}
            >
              <option value="days">{t("days")}</option>
              <option value="weeks">{t("weeks")}</option>
              <option value="months">{t("months")}</option>
              <option value="years">{t("years")}</option>
              <option value="permanent">{t("permanent")}</option>
            </select>
          </div>
          <button
            onClick={handleExtend}
            disabled={actionLoading}
            className="btn btn-sm"
            style={{ background: "#6366f1", color: "#fff" }}
          >
            {actionLoading
              ? <div className="spinner" style={{ width: 14, height: 14 }} />
              : <><RefreshCw size={14} /> {t("extend")}</>
            }
          </button>
        </div>
      )}

      {/* ── Expanded Details ── */}
      {expanded && (
        <div className="slide-down" style={{
          marginTop: "1rem", padding: "1rem", background: "var(--color-gray-50)",
          borderRadius: "var(--radius-md)", fontSize: "0.875rem"
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
            <div>
              <div className="text-muted text-xs" style={{ marginBottom: "0.2rem" }}>{t("lastLogin")}</div>
              <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <Clock size={14} /> {formatDate(doctor.lastLogin)}
              </div>
            </div>
            <div>
              <div className="text-muted text-xs" style={{ marginBottom: "0.2rem" }}>{t("expiresAt")}</div>
              <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <Calendar size={14} />
                {doctor.isPermanent
                  ? t("permanentAccount")
                  : doctor.accountExpiresAt
                    ? formatDate(doctor.accountExpiresAt)
                    : "—"
                }
              </div>
            </div>
            <div>
              <div className="text-muted text-xs" style={{ marginBottom: "0.2rem" }}>{t("accountDuration")}</div>
              <div style={{ fontWeight: 600 }}>
                {doctor.isPermanent
                  ? t("permanent")
                  : `${doctor.accountDurationValue || "—"} ${t(doctor.accountDurationUnit || "days")}`
                }
              </div>
            </div>
            <div>
              <div className="text-muted text-xs" style={{ marginBottom: "0.2rem" }}>
                {lang === "ar" ? "تاريخ الإنشاء" : "Created At"}
              </div>
              <div style={{ fontWeight: 600 }}>
                {formatDate(doctor.createdAt)}
              </div>
            </div>
            {daysLeft !== null && daysLeft > 0 && (
              <div>
                <div className="text-muted text-xs" style={{ marginBottom: "0.2rem" }}>
                  {lang === "ar" ? "الأيام المتبقية" : "Days Remaining"}
                </div>
                <div style={{
                  fontWeight: 700, fontSize: "1.25rem",
                  color: daysLeft <= 7 ? "#f59e0b" : daysLeft <= 3 ? "#ef4444" : "#22c55e"
                }}>
                  {daysLeft}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ADD DOCTOR TAB
   ═══════════════════════════════════════════════ */
function AddDoctorTab({ lang, t, profile }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
    durationValue: 1, durationUnit: "months"
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { type: "success"|"error", message }

  function updateField(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      // 1. Initialize a secondary Firebase app so we don't log out the Admin
      const secondaryApp = initializeApp(firebaseConfig, "SecondaryApp");
      const secondaryAuth = getAuth(secondaryApp);

      // 2. Create the user in Auth
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        form.email,
        form.password
      );
      const newUid = userCredential.user.uid;

      // 3. Prevent the secondary app from persisting the session
      await secondaryAuth.signOut();

      // 4. Calculate expiration date
      let accountExpiresAt = null;
      if (form.durationUnit !== "permanent" && form.durationValue > 0) {
        const now = new Date();
        const durationVal = parseInt(form.durationValue);
        switch (form.durationUnit) {
          case "days":
            accountExpiresAt = new Date(now.getTime() + durationVal * 24 * 60 * 60 * 1000);
            break;
          case "weeks":
            accountExpiresAt = new Date(now.getTime() + durationVal * 7 * 24 * 60 * 60 * 1000);
            break;
          case "months":
            accountExpiresAt = new Date(now);
            accountExpiresAt.setMonth(accountExpiresAt.getMonth() + durationVal);
            break;
          case "years":
            accountExpiresAt = new Date(now);
            accountExpiresAt.setFullYear(accountExpiresAt.getFullYear() + durationVal);
            break;
        }
      }

      // 5. Create the Firestore document using the main app's db
      await setDoc(doc(db, "users", newUid), {
        uid: newUid,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        role: "doctor",
        status: "active",
        subscription: null,
        accountExpiresAt: accountExpiresAt ? Timestamp.fromDate(accountExpiresAt) : null,
        accountDurationUnit: form.durationUnit || "permanent",
        accountDurationValue: parseInt(form.durationValue) || 0,
        isPermanent: form.durationUnit === "permanent",
        suspended: false,
        lastLogin: null,
        createdAt: serverTimestamp(),
        createdBy: profile?.uid || "admin",
      });

      setResult({ type: "success", message: t("doctorCreatedSuccess") });
      setForm({ name: "", email: "", phone: "", password: "", durationValue: 1, durationUnit: "months" });
    } catch (err) {
      console.error(err);
      const errorMsg = err.code === "auth/email-already-in-use" 
        ? (lang === "ar" ? "هذا البريد الإلكتروني مسجل مسبقاً" : "This email is already in use")
        : (err?.message || t("doctorCreatedError"));
      setResult({ type: "error", message: errorMsg });
    }

    setLoading(false);
  }

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <div className="card card-lg" style={{ borderTop: "3px solid #6366f1" }}>
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
            <UserPlus size={20} style={{ color: "#6366f1" }} />
            <h3 style={{ margin: 0 }}>{t("addDoctor")}</h3>
          </div>
          <p className="text-muted text-sm" style={{ margin: 0 }}>
            {lang === "ar"
              ? "أنشئ حساب طبيب جديد مع تحديد مدة الحساب"
              : "Create a new doctor account with a defined duration"
            }
          </p>
        </div>

        {result && (
          <div className={`alert ${result.type === "success" ? "alert-success" : "alert-error"}`}
            style={{ marginBottom: "1rem" }}>
            {result.type === "success" ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {result.message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="form-group">
            <label className="form-label">
              <Stethoscope size={14} style={{ display: "inline", marginLeft: "0.3rem", marginRight: "0.3rem" }} />
              {t("doctorName")}
            </label>
            <input
              className="form-input"
              type="text"
              value={form.name}
              onChange={e => updateField("name", e.target.value)}
              placeholder={lang === "ar" ? "د. أحمد محمد" : "Dr. Ahmed Mohamed"}
              required
            />
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label">
              <Mail size={14} style={{ display: "inline", marginLeft: "0.3rem", marginRight: "0.3rem" }} />
              {t("doctorEmail")}
            </label>
            <input
              className="form-input"
              type="email"
              dir="ltr"
              value={form.email}
              onChange={e => updateField("email", e.target.value)}
              placeholder="doctor@example.com"
              required
            />
          </div>

          {/* Phone */}
          <div className="form-group">
            <label className="form-label">
              <Phone size={14} style={{ display: "inline", marginLeft: "0.3rem", marginRight: "0.3rem" }} />
              {t("doctorPhone")}
            </label>
            <input
              className="form-input"
              type="tel"
              dir="ltr"
              value={form.phone}
              onChange={e => updateField("phone", e.target.value)}
              placeholder="0555123456"
            />
          </div>

          {/* Password */}
          <div className="form-group">
            <label className="form-label">
              <Lock size={14} style={{ display: "inline", marginLeft: "0.3rem", marginRight: "0.3rem" }} />
              {t("doctorPassword")}
            </label>
            <div style={{ position: "relative" }}>
              <input
                className="form-input"
                type={showPassword ? "text" : "password"}
                dir="ltr"
                value={form.password}
                onChange={e => updateField("password", e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
              />
              <button type="button" onClick={() => setShowPassword(s => !s)}
                style={{
                  position: "absolute", top: "50%",
                  left: lang === "ar" ? "0.75rem" : "auto",
                  right: lang === "ar" ? "auto" : "0.75rem",
                  transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer",
                  color: "var(--color-gray-400)"
                }}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Duration */}
          <div className="form-group">
            <label className="form-label">
              <Calendar size={14} style={{ display: "inline", marginLeft: "0.3rem", marginRight: "0.3rem" }} />
              {t("accountDuration")}
            </label>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              {form.durationUnit !== "permanent" && (
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  value={form.durationValue}
                  onChange={e => updateField("durationValue", e.target.value)}
                  style={{ width: 100 }}
                />
              )}
              <select
                className="form-select"
                value={form.durationUnit}
                onChange={e => updateField("durationUnit", e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="days">{t("days")}</option>
                <option value="weeks">{t("weeks")}</option>
                <option value="months">{t("months")}</option>
                <option value="years">{t("years")}</option>
                <option value="permanent">{t("permanent")}</option>
              </select>
            </div>
            <div className="form-hint">
              {form.durationUnit === "permanent"
                ? (lang === "ar" ? "سيكون الحساب دائماً نشطاً" : "Account will remain active permanently")
                : (lang === "ar"
                    ? `سينتهي الحساب بعد ${form.durationValue} ${t(form.durationUnit)}`
                    : `Account expires after ${form.durationValue} ${t(form.durationUnit).toLowerCase()}`
                  )
              }
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="btn btn-full"
            disabled={loading}
            style={{
              marginTop: "0.5rem",
              background: "linear-gradient(135deg, #4338ca, #6366f1)",
              color: "#fff",
              fontSize: "1rem"
            }}
          >
            {loading
              ? <div className="spinner" style={{ width: 20, height: 20 }} />
              : <><UserPlus size={18} /> {t("createDoctor")}</>
            }
          </button>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ALERTS TAB
   ═══════════════════════════════════════════════ */
function AlertsTab({ lang, t }) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "users"), where("role", "==", "doctor"));
    const unsub = onSnapshot(q, snap => {
      const allDoctors = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDoctors(allDoctors);
      setLoading(false);
    });
    return unsub;
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const now = new Date();
  const sevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  // Doctors expiring within 7 days
  const expiring = doctors.filter(d => {
    if (d.isPermanent || d.suspended) return false;
    if (!d.accountExpiresAt) return false;
    const exp = d.accountExpiresAt.toDate ? d.accountExpiresAt.toDate() : new Date(d.accountExpiresAt);
    return exp > now && exp <= sevenDays;
  }).sort((a, b) => {
    const aExp = a.accountExpiresAt.toDate ? a.accountExpiresAt.toDate() : new Date(a.accountExpiresAt);
    const bExp = b.accountExpiresAt.toDate ? b.accountExpiresAt.toDate() : new Date(b.accountExpiresAt);
    return aExp - bExp;
  });

  // Already expired but not yet suspended
  const expired = doctors.filter(d => {
    if (d.isPermanent) return false;
    if (!d.accountExpiresAt) return false;
    const exp = d.accountExpiresAt.toDate ? d.accountExpiresAt.toDate() : new Date(d.accountExpiresAt);
    return exp <= now;
  });

  return (
    <div>
      {/* Expiring Soon */}
      <div style={{ marginBottom: "2rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          <AlertTriangle size={20} style={{ color: "#f59e0b" }} />
          <h3 style={{ margin: 0 }}>
            {lang === "ar" ? "أطباء قارب انتهاء حسابهم (خلال 7 أيام)" : "Doctors Expiring Within 7 Days"}
          </h3>
          {expiring.length > 0 && (
            <span className="badge badge-warn">{expiring.length}</span>
          )}
        </div>

        {expiring.length === 0 ? (
          <div className="card text-center" style={{ padding: "2rem", color: "var(--color-gray-300)" }}>
            <CheckCircle size={32} style={{ margin: "0 auto 0.5rem", display: "block" }} />
            {lang === "ar" ? "لا يوجد أطباء قارب انتهاء حسابهم ✓" : "No doctors expiring soon ✓"}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {expiring.map(doctor => {
              const exp = doctor.accountExpiresAt.toDate ? doctor.accountExpiresAt.toDate() : new Date(doctor.accountExpiresAt);
              const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
              return (
                <div key={doctor.id} className="card card-sm" style={{
                  borderRight: "4px solid #f59e0b",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  flexWrap: "wrap", gap: "1rem"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: "50%",
                      background: "#fef3c7", display: "flex", alignItems: "center",
                      justifyContent: "center", color: "#f59e0b", fontWeight: 700
                    }}>
                      <AlertTriangle size={18} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700 }}>{doctor.name}</div>
                      <div className="text-muted text-sm">{doctor.email}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: lang === "ar" ? "left" : "right" }}>
                    <div style={{
                      fontWeight: 800, fontSize: "1.25rem",
                      color: days <= 3 ? "#ef4444" : "#f59e0b"
                    }}>
                      {days} {t("day")}
                    </div>
                    <div className="text-muted text-xs">{t("expiringIn")}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Already Expired */}
      {expired.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
            <XCircle size={20} style={{ color: "#ef4444" }} />
            <h3 style={{ margin: 0 }}>
              {lang === "ar" ? "حسابات منتهية" : "Expired Accounts"}
            </h3>
            <span className="badge badge-error">{expired.length}</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {expired.map(doctor => (
              <div key={doctor.id} className="card card-sm" style={{
                borderRight: "4px solid #ef4444", opacity: 0.8,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: "1rem"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: "#fef2f2", display: "flex", alignItems: "center",
                    justifyContent: "center", color: "#ef4444"
                  }}>
                    <XCircle size={18} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700 }}>{doctor.name}</div>
                    <div className="text-muted text-sm">{doctor.email}</div>
                  </div>
                </div>
                <span className="badge badge-error">{t("expired")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
